import React, { useState } from 'react';
import {
  Card,
  Table,
  Space,
  Typography,
  Select,
  DatePicker,
  Button,
  Tag,
  Input,
  Row,
  Col,
  Descriptions,
  Modal,
  Alert,
  Empty
} from 'antd';
import {
  FileTextOutlined,
  ReloadOutlined,
  EyeOutlined,
  FilterOutlined
} from '@ant-design/icons';
import { useQuery } from 'react-query';
import api from '../config/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface AuditLog {
  id: string;
  user_id: string;
  user_email: string;
  user_name: string;
  full_user_name?: string;
  action: string;
  entity_type: string;
  entity_id: string;
  entity_name: string;
  changes: any;
  description: string;
  ip_address: string;
  user_agent: string;
  created_at: string;
}

const AuditLogs: React.FC = () => {
  const [filters, setFilters] = useState({
    user_id: undefined as string | undefined,
    entity_type: undefined as string | undefined,
    action: undefined as string | undefined,
    start_date: undefined as string | undefined,
    end_date: undefined as string | undefined,
    page: 1,
    limit: 50
  });
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  // Fetch audit logs
  const { data, isLoading, refetch } = useQuery(
    ['audit-logs', filters],
    async () => {
      const params = new URLSearchParams();
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.entity_type) params.append('entity_type', filters.entity_type);
      if (filters.action) params.append('action', filters.action);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      params.append('page', filters.page.toString());
      params.append('limit', filters.limit.toString());

      const response = await api.get(`/api/audit-logs?${params.toString()}`);
      return response.data;
    },
    {
      keepPreviousData: true
    }
  );

  // Fetch users for filter - get all users by making multiple requests if needed
  const { data: usersData } = useQuery(
    'users-for-audit-filter',
    async () => {
      let allUsers: any[] = [];
      let page = 1;
      const limit = 100; // Max allowed by validation
      let hasMore = true;

      while (hasMore) {
        const response = await api.get('/api/users', {
          params: {
            page,
            limit
          }
        });
        
        const users = response.data?.users || [];
        allUsers = [...allUsers, ...users];
        
        // Check if we've fetched all users
        const total = response.data?.total || 0;
        if (allUsers.length >= total || users.length < limit) {
          hasMore = false;
        } else {
          page++;
        }
      }

      return allUsers;
    }
  );

  const logs: AuditLog[] = data?.logs || [];
  const total = data?.total || 0;

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filter changes
    }));
  };

  const handleDateRangeChange = (dates: any) => {
    if (dates && dates.length === 2) {
      setFilters(prev => ({
        ...prev,
        start_date: dates[0].startOf('day').toISOString(),
        end_date: dates[1].endOf('day').toISOString(),
        page: 1
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        start_date: undefined,
        end_date: undefined,
        page: 1
      }));
    }
  };

  const handleViewDetails = (log: AuditLog) => {
    setSelectedLog(log);
    setDetailModalVisible(true);
  };

  const getActionColor = (action: string) => {
    switch (action.toLowerCase()) {
      case 'create':
        return 'green';
      case 'update':
        return 'blue';
      case 'delete':
        return 'red';
      case 'login':
        return 'cyan';
      case 'logout':
        return 'default';
      default:
        return 'default';
    }
  };

  const getEntityTypeColor = (entityType: string) => {
    const colors: { [key: string]: string } = {
      user: 'purple',
      business: 'blue',
      ticket: 'orange',
      department: 'green',
      equipment: 'cyan',
      role: 'magenta'
    };
    return colors[entityType.toLowerCase()] || 'default';
  };

  const columns = [
    {
      title: 'Date & Time',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {dayjs(date).format('YYYY-MM-DD HH:mm:ss')}
        </Text>
      ),
      sorter: true
    },
    {
      title: 'User',
      key: 'user',
      width: 200,
      render: (_: any, record: AuditLog) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.full_user_name || record.user_name || record.user_email || 'Unknown'}</Text>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {record.user_email}
          </Text>
        </Space>
      )
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 100,
      render: (action: string) => (
        <Tag color={getActionColor(action)} style={{ textTransform: 'capitalize' }}>
          {action}
        </Tag>
      )
    },
    {
      title: 'Entity',
      key: 'entity',
      width: 150,
      render: (_: any, record: AuditLog) => (
        <Space direction="vertical" size={0}>
          <Tag color={getEntityTypeColor(record.entity_type)}>
            {record.entity_type}
          </Tag>
          {record.entity_name && (
            <Text type="secondary" style={{ fontSize: '11px' }}>
              {record.entity_name}
            </Text>
          )}
        </Space>
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text: string) => text || '-'
    },
    {
      title: 'IP Address',
      dataIndex: 'ip_address',
      key: 'ip_address',
      width: 130,
      render: (ip: string) => ip || '-'
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right' as const,
      render: (_: any, record: AuditLog) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record)}
          size="small"
        >
          Details
        </Button>
      )
    }
  ];

  return (
    <div style={{ padding: 24, background: '#f0f2f5', minHeight: '100vh' }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Title level={2} style={{ margin: 0 }}>
              <FileTextOutlined style={{ marginRight: 8 }} />
              Audit Logs
            </Title>
            <Text type="secondary">
              Track all system changes and user activities
            </Text>
          </div>

          <Alert
            message="Audit Logs"
            description="This page shows all changes made to the system, including who made the change, when it was made, and what was changed. Use the filters below to narrow down the results."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          {/* Filters */}
          <Card size="small" title={<><FilterOutlined /> Filters</>}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Text strong>User:</Text>
                <Select
                  style={{ width: '100%', marginTop: 8 }}
                  placeholder="All Users"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  value={filters.user_id}
                  onChange={(value) => handleFilterChange('user_id', value)}
                >
                  {usersData?.map((user: any) => (
                    <Option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} ({user.email})
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Text strong>Entity Type:</Text>
                <Select
                  style={{ width: '100%', marginTop: 8 }}
                  placeholder="All Types"
                  allowClear
                  value={filters.entity_type}
                  onChange={(value) => handleFilterChange('entity_type', value)}
                >
                  <Option value="user">User</Option>
                  <Option value="business">Business</Option>
                  <Option value="ticket">Ticket</Option>
                  <Option value="department">Department</Option>
                  <Option value="equipment">Equipment</Option>
                  <Option value="role">Role</Option>
                  <Option value="api_token">API Token</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Text strong>Action:</Text>
                <Select
                  style={{ width: '100%', marginTop: 8 }}
                  placeholder="All Actions"
                  allowClear
                  value={filters.action}
                  onChange={(value) => handleFilterChange('action', value)}
                >
                  <Option value="create">Create</Option>
                  <Option value="update">Update</Option>
                  <Option value="delete">Delete</Option>
                  <Option value="login">Login</Option>
                  <Option value="logout">Logout</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Text strong>Date Range:</Text>
                <RangePicker
                  style={{ width: '100%', marginTop: 8 }}
                  onChange={handleDateRangeChange}
                  showTime
                />
              </Col>
            </Row>
            <Row style={{ marginTop: 16 }}>
              <Col>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    setFilters({
                      user_id: undefined,
                      entity_type: undefined,
                      action: undefined,
                      start_date: undefined,
                      end_date: undefined,
                      page: 1,
                      limit: 50
                    });
                  }}
                >
                  Clear Filters
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Table */}
          <Card>
            <Table
              columns={columns}
              dataSource={logs}
              rowKey="id"
              loading={isLoading}
              pagination={{
                current: filters.page,
                pageSize: filters.limit,
                total: total,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} logs`,
                onChange: (page, pageSize) => {
                  setFilters(prev => ({
                    ...prev,
                    page,
                    limit: pageSize || 50
                  }));
                }
              }}
              scroll={{ x: 1200 }}
              locale={{
                emptyText: <Empty description="No audit logs found" />
              }}
            />
          </Card>
        </Space>
      </Card>

      {/* Detail Modal */}
      <Modal
        title="Audit Log Details"
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedLog(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setDetailModalVisible(false);
            setSelectedLog(null);
          }}>
            Close
          </Button>
        ]}
        width={800}
      >
        {selectedLog && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="Date & Time">
              {dayjs(selectedLog.created_at).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="User">
              <Space direction="vertical" size={0}>
                <Text strong>{selectedLog.full_user_name || selectedLog.user_name || selectedLog.user_email || 'Unknown'}</Text>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {selectedLog.user_email}
                </Text>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Action">
              <Tag color={getActionColor(selectedLog.action)} style={{ textTransform: 'capitalize' }}>
                {selectedLog.action}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Entity Type">
              <Tag color={getEntityTypeColor(selectedLog.entity_type)}>
                {selectedLog.entity_type}
              </Tag>
            </Descriptions.Item>
            {selectedLog.entity_name && (
              <Descriptions.Item label="Entity Name">
                {selectedLog.entity_name}
              </Descriptions.Item>
            )}
            {selectedLog.entity_id && (
              <Descriptions.Item label="Entity ID">
                <Text code>{selectedLog.entity_id}</Text>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Description">
              {selectedLog.description || '-'}
            </Descriptions.Item>
            {selectedLog.changes && (
              <Descriptions.Item label="Changes">
                <pre style={{ 
                  background: '#f5f5f5', 
                  padding: 12, 
                  borderRadius: 4,
                  maxHeight: 300,
                  overflow: 'auto',
                  fontSize: '12px'
                }}>
                  {JSON.stringify(selectedLog.changes, null, 2)}
                </pre>
              </Descriptions.Item>
            )}
            {selectedLog.ip_address && (
              <Descriptions.Item label="IP Address">
                <Text code>{selectedLog.ip_address}</Text>
              </Descriptions.Item>
            )}
            {selectedLog.user_agent && (
              <Descriptions.Item label="User Agent">
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {selectedLog.user_agent}
                </Text>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default AuditLogs;

